<template>
  <v-dialog v-model="isOpen" max-width="733" persistent>
    <v-card>
      <v-card-title class="d-flex align-center text-subtitle-1">
        <v-icon class="mr-2">mdi-file-excel-outline</v-icon>
        {{ $t("dangerousGoods.dangerousGoodsList.export.title") }}
        <div class="flex-grow-1"></div>
        <v-btn icon @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <div v-if="mode === 'locations'">
          <div class="d-flex align-center mb-2 pt-1">
            <div class="subtitle-2">
              {{ $t("dangerousGoods.dangerousGoodsList.export.chooseLocations") }}
            </div>
            <v-spacer />
            <v-btn
              small
              outlined
              color="primary"
              @click="toggleAllLocations"
            >
              {{ locationToggleLabel }}
            </v-btn>
          </div>
          <div class="check-columns">
            <v-checkbox
              v-for="location in storageLocations"
              :key="location._id || location.id"
              v-model="selectedLocationIds"
              :label="location.name"
              :value="location._id || location.id"
              dense
              hide-details
              class="export-check ma-0"
            ></v-checkbox>
          </div>
          <v-divider class="my-4" />
        </div>

        <div class="d-flex align-center mb-2 pt-1">
          <div class="subtitle-2">
            {{ $t("dangerousGoods.dangerousGoodsList.export.chooseFields") }}
          </div>
          <v-spacer />
          <v-btn small outlined color="primary" @click="toggleAllFields">
            {{ fieldToggleLabel }}
          </v-btn>
        </div>
        <div class="check-columns">
          <v-checkbox
            v-for="field in availableFields"
            :key="field.key"
            v-model="selectedFieldKeys"
            :label="field.label"
            :value="field.key"
            dense
            hide-details
            class="export-check ma-0"
          ></v-checkbox>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="loading">
          {{ $t("general.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!canExport"
          @click="runExport"
        >
          <v-icon left>mdi-download</v-icon>
          {{ $t("dangerousGoods.dangerousGoodsList.export.download") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
//keys must match the backend EXPORT_FIELDS whitelist
const FIELD_DEFS = [
  { key: "identifier", labelKey: "form.identifier" },
  { key: "name", labelKey: "form.name" },
  { key: "storageLocation", labelKey: "export.field.storageLocation" },
  { key: "pictograms", labelKey: "tableHeader.pictograms" },
  { key: "h", labelKey: "export.field.h" },
  { key: "p", labelKey: "export.field.p" },
  { key: "storageClassification", labelKey: "form.storageClassification" },
  { key: "waterHazardClass", labelKey: "form.waterHazardClass" },
  { key: "containerSize", labelKey: "form.containerSize" },
  { key: "yearlyUsage", labelKey: "form.yearlyUsage" },
  { key: "usageLocation", labelKey: "form.usageLocation" },
  { key: "manufacturer", labelKey: "form.manufacturer" },
  { key: "responsiblePerson", labelKey: "form.responsiblePerson" },
  { key: "fss7Relevant", labelKey: "form.fss7Relevant" },
  { key: "safetyDataSheetDate", labelKey: "form.safetyDataSheetDate" },
  { key: "operatingInstruction", labelKey: "tableHeader.operatingInstruction" },
];

export default {
  props: {
    //"locations" shows the location picker, "items" uses the current location
    mode: { type: String, default: "items" },
    storageLocations: { type: Array, default: () => [] },
    currentStorageLocationId: { type: String, default: "" },
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      selectedLocationIds: [],
      selectedFieldKeys: FIELD_DEFS.map((f) => f.key),
    };
  },
  computed: {
    availableFields() {
      return FIELD_DEFS.filter(
        (field) =>
          this.mode === "locations" || field.key !== "storageLocation",
      ).map((field) => ({
        key: field.key,
        label: this.$t(`dangerousGoods.dangerousGoodsList.${field.labelKey}`),
      }));
    },
    allFieldsSelected() {
      //only the fields actually shown count, storageLocation is hidden in items mode
      return this.availableFields.every((field) =>
        this.selectedFieldKeys.includes(field.key),
      );
    },
    fieldToggleLabel() {
      return this.allFieldsSelected
        ? this.$t("dangerousGoods.dangerousGoodsList.export.deselectAll")
        : this.$t("dangerousGoods.dangerousGoodsList.export.selectAll");
    },
    allLocationsSelected() {
      return (
        this.storageLocations.length > 0 &&
        this.selectedLocationIds.length === this.storageLocations.length
      );
    },
    locationToggleLabel() {
      return this.allLocationsSelected
        ? this.$t("dangerousGoods.dangerousGoodsList.export.deselectAll")
        : this.$t("dangerousGoods.dangerousGoodsList.export.selectAll");
    },
    canExport() {
      const hasLocations =
        this.mode === "items" || this.selectedLocationIds.length > 0;
      return hasLocations && this.selectedFieldKeys.length > 0 && !this.loading;
    },
  },
  methods: {
    open() {
      if (this.mode === "locations") {
        this.selectedLocationIds = this.storageLocations.map(
          (l) => l._id || l.id,
        );
      }
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
    },
    toggleAllFields() {
      this.selectedFieldKeys = this.allFieldsSelected
        ? []
        : this.availableFields.map((f) => f.key);
    },
    toggleAllLocations() {
      this.selectedLocationIds = this.allLocationsSelected
        ? []
        : this.storageLocations.map((l) => l._id || l.id);
    },
    //preserve the on-screen field order in the sheet columns
    orderedFields() {
      return this.availableFields.filter((field) =>
        this.selectedFieldKeys.includes(field.key),
      );
    },
    async runExport() {
      if (!this.canExport) return;

      this.loading = true;
      try {
        const storageLocationIds =
          this.mode === "locations"
            ? this.selectedLocationIds
            : [this.currentStorageLocationId];

        const result = await this.$store.dispatch("exportDangerousGoods", {
          storageLocationIds,
          fields: this.orderedFields(),
        });

        if (result?.base64) {
          this.download(result.base64);
          this.close();
        }
      } finally {
        this.loading = false;
      }
    },
    download(base64) {
      const link = document.createElement("a");
      link.href =
        "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
        base64;
      link.download = `${this.$t(
        "dangerousGoods.dangerousGoodsList.export.fileName",
      )}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.check-columns {
  column-count: 2;
  column-gap: 24px;
}
@media (max-width: 599px) {
  .check-columns {
    column-count: 1;
  }
}
.export-check {
  break-inside: avoid;
  padding-top: 6px;
}
.export-check >>> .v-input__slot {
  margin-bottom: 0;
}
.export-check >>> .v-label {
  line-height: 1.2;
}
</style>
